import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPhotoPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { pomanjsaj } from '../api/slike';
import { useSlikaApi } from '../api/useSlikaApi';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { useSeja } from '../seja/useSeja';
import { barve } from '../tema';
import { KLJUC_UREJANJA_SLIK, VRSTE_OZADJA, type VrstaOzadja } from './urejanjeSlik';

type Nastavitve = Shema['UrejanjeSlikNastavitve'];
type Ozadje = Shema['OzadjeSlik'];

/**
 * Nastavitve → Urejanje slik (novo v spletu): privzete izbire za urejanje fotografij vozil z umetno inteligenco
 * (tablica z logotipom, moteči predmeti, svetloba, ozadje), lastna ozadja podjetja (npr. salon), navodila za vse slike
 * in oznaka urejenih slik na javni ponudbi. Pri sliki vozila se izbire lahko spremenijo.
 */
export function UrejanjeSlik() {
  const poizvedba = useQuery({
    queryKey: KLJUC_UREJANJA_SLIK,
    queryFn: ({ signal }) => podatki(api.GET('/api/urejanje-slik', { signal })),
  });
  if (!poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Nastavitev urejanja slik ni bilo mogoče naložiti"
      />
    );
  }
  return <Obrazec shranjeno={poizvedba.data} />;
}

function Obrazec({ shranjeno }: { shranjeno: Nastavitve }) {
  const odjemalec = useQueryClient();
  const seja = useSeja();
  const ai = seja.status === 'prijavljen' && !!seja.instanca.umetnaInteligenca;
  const [n, setN] = useState({
    tablica: !!shranjeno.tablica,
    predmeti: !!shranjeno.predmeti,
    svetloba: !!shranjeno.svetloba,
    ozadje: (shranjeno.ozadje ?? 'NESPREMENJENO') as VrstaOzadja,
    ozadjeId: shranjeno.ozadjeId ?? null,
    navodila: shranjeno.navodila ?? '',
    oznakaAi: !!shranjeno.oznakaAi,
  });
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const ozadja = shranjeno.ozadja ?? [];
  const ozadjeId = n.ozadjeId != null && ozadja.some((o) => o.id === n.ozadjeId) ? n.ozadjeId : (ozadja[0]?.id ?? null);
  const spremenjeno =
    n.tablica !== !!shranjeno.tablica ||
    n.predmeti !== !!shranjeno.predmeti ||
    n.svetloba !== !!shranjeno.svetloba ||
    n.ozadje !== (shranjeno.ozadje ?? 'NESPREMENJENO') ||
    ozadjeId !== (shranjeno.ozadjeId ?? null) ||
    n.navodila !== (shranjeno.navodila ?? '') ||
    n.oznakaAi !== !!shranjeno.oznakaAi;
  const nastavi = <K extends keyof typeof n>(k: K, v: (typeof n)[K]) => setN((x) => ({ ...x, [k]: v }));

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setDela(true);
    try {
      odjemalec.setQueryData(
        KLJUC_UREJANJA_SLIK,
        await podatki(api.PUT('/api/urejanje-slik', { body: { ...n, ozadjeId: ozadjeId ?? undefined } })),
      );
      notifications.show({ color: 'green', message: 'Nastavitve urejanja slik so shranjene.' });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka('Ni shranjeno: popravite označena polja.');
      } else {
        setNapaka(`Ni shranjeno: ${sporocilo(e)}`);
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="md">
      {!ai && (
        <Alert color="yellow" variant="light">
          Umetna inteligenca za vaše podjetje trenutno ni na voljo ali je izklopljena (Nastavitve → Umetna inteligenca).
          Nastavitve lahko pripravite že zdaj.
        </Alert>
      )}
      <Kartica naslov="Kaj naj umetna inteligenca uredi">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Privzete izbire za vse slike; pri posamezni sliki jih lahko spremenite. Vozilo samo ostane nespremenjeno –
            umetna inteligenca ne popravlja prask, barve ali opreme.
          </Text>
          <Checkbox
            label="Registrsko tablico zamenjaj z logotipom podjetja"
            description={
              shranjeno.logotip ? (
                'Uporabi se logotip iz Nastavitve → Logotip.'
              ) : (
                <>
                  Podjetje nima logotipa, zato bo tablica prazna.{' '}
                  <Anchor component={Link} to="/nastavitve/logotip" size="xs">
                    Naložite logotip
                  </Anchor>
                </>
              )
            }
            checked={n.tablica}
            onChange={(e) => nastavi('tablica', e.currentTarget.checked)}
          />
          <Checkbox
            label="Odstrani moteče predmete"
            description="Ljudi, druga vozila v ospredju, smeti, listke za vetrobranskim steklom …"
            checked={n.predmeti}
            onChange={(e) => nastavi('predmeti', e.currentTarget.checked)}
          />
          <Checkbox
            label="Izboljšaj svetlobo"
            description="Svetlejša, jasnejša slika z naravnimi barvami."
            checked={n.svetloba}
            onChange={(e) => nastavi('svetloba', e.currentTarget.checked)}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Ozadje
            </Text>
            <SegmentedControl
              data={VRSTE_OZADJA}
              value={n.ozadje}
              onChange={(v) => nastavi('ozadje', v as VrstaOzadja)}
              fullWidth
            />
            <Text size="xs" c="dimmed">
              {n.ozadje === 'STUDIO'
                ? 'Svetlo siv fotografski studio z mehko svetlobo in odsevom na tleh.'
                : n.ozadje === 'LASTNO'
                  ? 'Vozilo se postavi v prostor z vaše fotografije (izberite jo spodaj).'
                  : 'Ozadje fotografije ostane (moteči predmeti se lahko odstranijo).'}
            </Text>
            {polja.ozadje && (
              <Text size="xs" c="red">
                {polja.ozadje}
              </Text>
            )}
          </Stack>
        </Stack>
      </Kartica>

      <LastnaOzadja
        ozadja={ozadja}
        izbrano={n.ozadje === 'LASTNO' ? ozadjeId : null}
        izberi={(id) => setN((x) => ({ ...x, ozadje: 'LASTNO', ozadjeId: id }))}
        napaka={polja.ozadjeId}
      />

      <Kartica naslov="Navodila za vse slike">
        <Textarea
          size="md"
          label="Vaša navodila (neobvezno)"
          description="Dodatne želje za vse slike. Pri posamezni sliki lahko dodate še svoja navodila."
          placeholder="npr. topli toni, vozilo naj bo v središču, odstrani odseve v steklih"
          value={n.navodila}
          onChange={(e) => nastavi('navodila', e.currentTarget.value)}
          error={polja.navodila}
          autosize
          minRows={3}
          maxRows={10}
        />
      </Kartica>

      <Kartica naslov="Javna ponudba">
        <Checkbox
          label="Pri urejenih slikah pokaži oznako »Urejeno z AI«"
          description="Majhna oznaka v kotu slike na javni strani vozila. Slika sama ostane brez oznake."
          checked={n.oznakaAi}
          onChange={(e) => nastavi('oznakaAi', e.currentTarget.checked)}
        />
      </Kartica>

      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group>
        <Button onClick={() => void shrani()} loading={dela} disabled={!spremenjeno}>
          Shrani
        </Button>
      </Group>
    </Stack>
  );
}

/** Sličica ozadja s strežnika. */
export function SlicicaOzadja({ ozadje }: { ozadje: Ozadje }) {
  const url = useSlikaApi(`${ozadje.id}`, (signal) =>
    api.GET('/api/urejanje-slik/ozadja/{id}', {
      signal,
      params: { path: { id: ozadje.id! }, query: { mala: true } },
      parseAs: 'blob',
    }),
  );
  return (
    <Center style={{ aspectRatio: '4 / 3', background: barve.izbrano, borderRadius: 8, overflow: 'hidden' }}>
      {url ? (
        <img
          src={url}
          alt={ozadje.naziv}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Loader size="sm" />
      )}
    </Center>
  );
}

/**
 * Lastna ozadja podjetja: dodajanje (strežnik pomanjša), preimenovanje ob izhodu iz polja, brisanje in izbira
 * privzetega (klik na sliko).
 */
function LastnaOzadja({
  ozadja,
  izbrano,
  izberi,
  napaka,
}: {
  ozadja: Ozadje[];
  izbrano: number | null;
  izberi: (id: number) => void;
  napaka?: string;
}) {
  const odjemalec = useQueryClient();
  const izbira = useRef<HTMLInputElement>(null);
  const [nalagam, setNalagam] = useState(false);
  const [napake, setNapake] = useState<string | null>(null);

  // neshranjene izbire v obrazcu ostanejo (obrazec ima svoje stanje), osvežijo se ozadja
  const osvezi = async () => odjemalec.setQueryData(KLJUC_UREJANJA_SLIK, await podatki(api.GET('/api/urejanje-slik')));

  const dodaj = async (f: File | undefined) => {
    if (!f) {
      return;
    }
    setNapake(null);
    setNalagam(true);
    try {
      const telo = new FormData();
      telo.append('slika', await pomanjsaj(f));
      telo.append('naziv', f.name.replace(/\.[^.]+$/, '').slice(0, 100));
      await podatki(api.POST('/api/urejanje-slik/ozadja', { body: telo as unknown as { slika: string } }));
      await osvezi();
    } catch (e) {
      setNapake(e instanceof NapakaApi && e.polja.slika ? e.polja.slika : sporocilo(e));
    } finally {
      setNalagam(false);
    }
  };

  const preimenuj = async (o: Ozadje, naziv: string) => {
    if (naziv.trim() === (o.naziv ?? '') || !naziv.trim()) {
      return;
    }
    try {
      await podatki(
        api.PUT('/api/urejanje-slik/ozadja/{id}', { params: { path: { id: o.id! } }, body: { ...o, naziv } }),
      );
      await osvezi();
    } catch (e) {
      setNapake(e instanceof NapakaApi && e.polja.naziv ? e.polja.naziv : sporocilo(e));
    }
  };

  const izbrisi = async (o: Ozadje) => {
    setNapake(null);
    try {
      await izvedi(api.DELETE('/api/urejanje-slik/ozadja/{id}', { params: { path: { id: o.id! } } }));
      await osvezi();
    } catch (e) {
      setNapake(sporocilo(e));
    }
  };

  return (
    <Kartica
      naslov="Lastna ozadja"
      dejanja={
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPhotoPlus size={16} />}
          onClick={() => izbira.current?.click()}
          loading={nalagam}
          disabled={ozadja.length >= 10}
        >
          Dodaj ozadje
        </Button>
      }
    >
      <input
        ref={izbira}
        type="file"
        accept="image/jpeg,image/png,image/*"
        hidden
        aria-label="Izberi fotografijo ozadja"
        onChange={(e) => {
          void dodaj(e.currentTarget.files?.[0]);
          e.currentTarget.value = '';
        }}
      />
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Fotografija vašega prodajnega salona ali dvorišča, v katero umetna inteligenca postavi vozilo. Najboljša je
          ležeča fotografija praznega prostora brez vozil, posneta v višini avtomobila. Največ 10 ozadij; klik na sliko
          jo izbere kot privzeto.
        </Text>
        {ozadja.length === 0 ? (
          <UnstyledButton
            onClick={() => izbira.current?.click()}
            p="lg"
            style={{ border: `2px dashed ${barve.obroba}`, borderRadius: 10, textAlign: 'center' }}
          >
            <Stack align="center" gap={4}>
              <IconPhotoPlus size={32} color={barve.besediloSekundarno} />
              <Text size="sm" fw={600}>
                Dodajte fotografijo ozadja
              </Text>
            </Stack>
          </UnstyledButton>
        ) : (
          <SimpleGrid cols={{ base: 2, xs: 3, md: 4 }} spacing="sm">
            {ozadja.map((o) => {
              const izbran = izbrano === o.id;
              return (
                <Stack key={o.id} gap={4}>
                  <UnstyledButton
                    onClick={() => izberi(o.id!)}
                    aria-label={`Izberi ozadje ${o.naziv}`}
                    aria-pressed={izbran}
                    pos="relative"
                    style={{
                      borderRadius: 10,
                      padding: 3,
                      outline: izbran ? `3px solid ${barve.primarna}` : `1px solid ${barve.obroba}`,
                    }}
                  >
                    <SlicicaOzadja ozadje={o} />
                    {izbran && (
                      <Badge size="xs" variant="filled" style={{ position: 'absolute', left: 8, top: 8 }}>
                        Privzeto
                      </Badge>
                    )}
                  </UnstyledButton>
                  <Group gap={4} wrap="nowrap">
                    <TextInput
                      size="xs"
                      aria-label="Naziv ozadja"
                      defaultValue={o.naziv}
                      onBlur={(e) => void preimenuj(o, e.currentTarget.value)}
                      maxLength={100}
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    <Tooltip label="Izbriši ozadje">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Izbriši ozadje ${o.naziv}`}
                        onClick={() => void izbrisi(o)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Stack>
              );
            })}
          </SimpleGrid>
        )}
        {(napaka || napake) && (
          <Box>
            <Text size="sm" c="red" role="alert">
              {napake ?? napaka}
            </Text>
          </Box>
        )}
      </Stack>
    </Kartica>
  );
}
